import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from "react";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, TextField } from "@mui/material";

// Enhanced types to support different dialog modes
type ConfirmMode = "confirm" | "prompt";

interface ConfirmOptions {
  title?: string;
  message: string;
  mode?: ConfirmMode;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
}

type ConfirmContextType = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: ConfirmOptions) => Promise<string | null>;
};

interface ConfirmProviderProps {
  children: ReactNode;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  mode: ConfirmMode;
  confirmText: string;
  cancelText: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const [inputValue, setInputValue] = useState<string>("");

  // Use ref to store the resolve function to avoid stale closures
  const resolveRef = useRef<((value: any) => void) | null>(null);

  const showDialog = useCallback((options: ConfirmOptions, mode: ConfirmMode): Promise<any> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      const defaultValue = options.defaultValue || "";
      setInputValue(defaultValue);
      setConfirmState({
        open: true,
        title: options.title || "Are you sure?",
        message: options.message,
        mode,
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        placeholder: options.placeholder,
        defaultValue,
        required: options.required,
      });
    });
  }, []);

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      return showDialog(options, "confirm");
    },
    [showDialog]
  );

  const prompt = useCallback(
    (options: ConfirmOptions): Promise<string | null> => {
      return showDialog(options, "prompt");
    },
    [showDialog]
  );

  const closeDialog = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    setInputValue("");
  }, []);

  const handleClose = useCallback(() => {
    if (resolveRef.current) {
      if (confirmState.mode === "prompt") {
        resolveRef.current(null);
      } else {
        resolveRef.current(false);
      }
      resolveRef.current = null;
    }
    closeDialog();
  }, [confirmState.mode, closeDialog]);

  const handleConfirm = useCallback(() => {
    // Check if required field is empty for prompt mode
    if (confirmState.mode === "prompt" && confirmState.required && !inputValue.trim()) {
      return; // Don't close, validation failed
    }

    if (resolveRef.current) {
      if (confirmState.mode === "prompt") {
        resolveRef.current(inputValue);
      } else {
        resolveRef.current(true);
      }
      resolveRef.current = null;
    }
    closeDialog();
  }, [confirmState.mode, confirmState.required, inputValue, closeDialog]);

  // Memoize the input change handler to prevent unnecessary re-renders
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const isConfirmDisabled = confirmState.mode === "prompt" && confirmState.required && !inputValue.trim();

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={confirmState.open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "NotoSans" }}>{confirmState.title}</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ fontFamily: "NotoSans", whiteSpace: "pre-line", mb: confirmState.mode === "prompt" ? 2 : 0 }}
          >
            {confirmState.message}
          </DialogContentText>
          {confirmState.mode === "prompt" && (
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder={confirmState.placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              required={confirmState.required}
              error={confirmState.required && !inputValue.trim()}
              helperText={confirmState.required && !inputValue.trim() ? "This field is required" : ""}
              sx={{ fontFamily: "NotoSans" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ fontFamily: "NotoSans" }} onClick={handleClose}>
            {confirmState.cancelText}
          </Button>
          <Button
            sx={{ fontFamily: "NotoSans" }}
            onClick={handleConfirm}
            color={confirmState.mode === "confirm" ? "error" : "primary"}
            autoFocus={confirmState.mode !== "prompt"}
            disabled={isConfirmDisabled}
          >
            {confirmState.confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
}

export function useConfirmDialog() {
  const { confirm } = useConfirm();
  return confirm;
}

export function usePromptDialog() {
  const { prompt } = useConfirm();
  return prompt;
}
